import { AspectRatio, Box, Flex, Paper, Text, useMantineTheme } from "@mantine/core";
import { useMemo } from "react";
import { useElementSize } from "@mantine/hooks";
import { resolveChartAccent, resolveEyeSeriesColor } from "@constants/resultColors";
import { OcularMotilityData } from "types/testTypes";
import { useTranslation } from "react-i18next";

export interface GazeExtent {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export function computeGazeExtent(
  data: OcularMotilityData[] | null | undefined,
  visibleEye: "left" | "right" | "both" = "both",
): GazeExtent {
  let minX = Infinity,
    minY = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity;

  data?.forEach((point) => {
    if (visibleEye !== "right" && point.leftGazePosition) {
      minX = Math.min(minX, point.leftGazePosition.x);
      maxX = Math.max(maxX, point.leftGazePosition.x);
      minY = Math.min(minY, point.leftGazePosition.y);
      maxY = Math.max(maxY, point.leftGazePosition.y);
    }

    if (visibleEye !== "left" && point.rightGazePosition) {
      minX = Math.min(minX, point.rightGazePosition.x);
      maxX = Math.max(maxX, point.rightGazePosition.x);
      minY = Math.min(minY, point.rightGazePosition.y);
      maxY = Math.max(maxY, point.rightGazePosition.y);
    }

    if (point.targetPosition) {
      minX = Math.min(minX, point.targetPosition.x);
      maxX = Math.max(maxX, point.targetPosition.x);
      minY = Math.min(minY, point.targetPosition.y);
      maxY = Math.max(maxY, point.targetPosition.y);
    }
  });

  return { minX, maxX, minY, maxY };
}

export function mergeGazeExtents(extents: GazeExtent[]): GazeExtent {
  return extents.reduce(
    (acc, extent) => ({
      minX: Math.min(acc.minX, extent.minX),
      maxX: Math.max(acc.maxX, extent.maxX),
      minY: Math.min(acc.minY, extent.minY),
      maxY: Math.max(acc.maxY, extent.maxY),
    }),
    { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity },
  );
}

export default function CircularPursuitGraph(props) {
  const {
    data,
    compact = false,
    height: customHeight = 500,
    showPaper = true,
    showLegend = true,
    visibleEye = "both",
    title,
    sharedExtent,
  } = props as {
    data: OcularMotilityData[] | null;
    compact?: boolean;
    height?: number;
    showPaper?: boolean;
    showLegend?: boolean;
    visibleEye?: "left" | "right" | "both";
    title?: string;
    sharedExtent?: GazeExtent;
  };
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const { ref, width, height } = useElementSize();

  const LEFT_GAZE_COLOR = resolveEyeSeriesColor(theme, "left");
  const RIGHT_GAZE_COLOR = resolveEyeSeriesColor(theme, "right");
  const TARGET_COLOR = resolveChartAccent(theme, "target");
  const GAZE_RADIUS = 3;
  const TARGET_RADIUS = 2;
  const PATH_WIDTH = 1.5;

  const svgContent = useMemo(() => {
    if (!data?.length) return null;

    const size = Math.min(width || customHeight, height || customHeight);
    const svgWidth = size;
    const svgHeight = size;

    let { minX, maxX, minY, maxY } = sharedExtent ?? computeGazeExtent(data, visibleEye);

    // Make the coordinate range square to preserve aspect ratio
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const maxRange = Math.max(rangeX, rangeY) * 1.1; // Add 10% padding

    // Recenter the data in a square viewport
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    minX = centerX - maxRange / 2;
    maxX = centerX + maxRange / 2;
    minY = centerY - maxRange / 2;
    maxY = centerY + maxRange / 2;

    const padding = 20;
    const effectiveSize = size - padding * 2;

    // Normalization functions - using the adjusted square range
    const normalizeX = (x) => padding + (effectiveSize * (x - minX)) / (maxX - minX || 1);
    const normalizeY = (y) =>
      padding + effectiveSize - (effectiveSize * (y - minY)) / (maxY - minY || 1);

    // Generate left gaze path
    let leftGazePath = "";
    data?.forEach((point, i) => {
      if (visibleEye !== "right" && point.leftGazePosition) {
        const x = normalizeX(point.leftGazePosition.x);
        const y = normalizeY(point.leftGazePosition.y);
        if (i === 0 || leftGazePath === "") {
          leftGazePath += `M ${x} ${y}`;
        } else {
          leftGazePath += ` L ${x} ${y}`;
        }
      }
    });

    // Generate right gaze path
    let rightGazePath = "";
    data?.forEach((point, i) => {
      if (visibleEye !== "left" && point.rightGazePosition) {
        const x = normalizeX(point.rightGazePosition.x);
        const y = normalizeY(point.rightGazePosition.y);
        if (i === 0 || rightGazePath === "") {
          rightGazePath += `M ${x} ${y}`;
        } else {
          rightGazePath += ` L ${x} ${y}`;
        }
      }
    });

    // Generate target path
    let targetPath = "";
    data?.forEach((point, i) => {
      if (point.targetPosition) {
        const x = normalizeX(point.targetPosition.x);
        const y = normalizeY(point.targetPosition.y);
        if (i === 0 || targetPath === "") {
          targetPath += `M ${x} ${y}`;
        } else {
          targetPath += ` L ${x} ${y}`;
        }
      }
    });

    // Generate gaze and target position markers (limited to avoid clutter)
    const leftGazeMarkers = [];
    const rightGazeMarkers = [];
    const targetMarkers = [];

    // Sampling factor - only show a subset of points
    const samplingFactor = Math.max(1, Math.floor(data?.length / 50));

    data?.forEach((point, index) => {
      // Show first, last, and sampled points
      const isKeyPoint = index === 0 || index === data?.length - 1 || index % samplingFactor === 0;

      if (isKeyPoint && visibleEye !== "right" && point.leftGazePosition) {
        leftGazeMarkers.push(
          <circle
            key={`leftGaze-${index}`}
            cx={normalizeX(point.leftGazePosition.x)}
            cy={normalizeY(point.leftGazePosition.y)}
            r={GAZE_RADIUS}
            fill={LEFT_GAZE_COLOR}
            opacity={index === data?.length - 1 ? 1 : 0.6} // Make the latest point more visible
          />,
        );
      }

      if (isKeyPoint && visibleEye !== "left" && point.rightGazePosition) {
        rightGazeMarkers.push(
          <circle
            key={`rightGaze-${index}`}
            cx={normalizeX(point.rightGazePosition.x)}
            cy={normalizeY(point.rightGazePosition.y)}
            r={GAZE_RADIUS}
            fill={RIGHT_GAZE_COLOR}
            opacity={index === data?.length - 1 ? 1 : 0.6} // Make the latest point more visible
          />,
        );
      }

      if (isKeyPoint && point.targetPosition) {
        targetMarkers.push(
          <circle
            key={`target-${index}`}
            cx={normalizeX(point.targetPosition.x)}
            cy={normalizeY(point.targetPosition.y)}
            r={TARGET_RADIUS}
            fill={TARGET_COLOR}
            opacity={index === data?.length - 1 ? 1 : 0.6} // Make the latest point more visible
          />,
        );
      }
    });

    // Latest positions (if available)
    const latestPoint = data ? data[data.length - 1] : null;
    let latestLeftGazePosition = null;
    let latestRightGazePosition = null;
    let latestTargetPosition = null;

    if (latestPoint) {
      if (visibleEye !== "right" && latestPoint.leftGazePosition) {
        latestLeftGazePosition = (
          <circle
            cx={normalizeX(latestPoint.leftGazePosition.x)}
            cy={normalizeY(latestPoint.leftGazePosition.y)}
            r={GAZE_RADIUS * 1.5}
            fill="none"
            stroke={LEFT_GAZE_COLOR}
            strokeWidth={2}
          />
        );
      }

      if (visibleEye !== "left" && latestPoint.rightGazePosition) {
        latestRightGazePosition = (
          <circle
            cx={normalizeX(latestPoint.rightGazePosition.x)}
            cy={normalizeY(latestPoint.rightGazePosition.y)}
            r={GAZE_RADIUS * 1.5}
            fill="none"
            stroke={RIGHT_GAZE_COLOR}
            strokeWidth={2}
          />
        );
      }

      if (latestPoint.targetPosition) {
        latestTargetPosition = (
          <circle
            cx={normalizeX(latestPoint.targetPosition.x)}
            cy={normalizeY(latestPoint.targetPosition.y)}
            r={TARGET_RADIUS * 1.5}
            fill="none"
            stroke={TARGET_COLOR}
            strokeWidth={2}
          />
        );
      }
    }

    return (
      <svg
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ position: "absolute", top: 0, left: 0 }}
      >
        {visibleEye !== "right" && (
          <path d={leftGazePath} stroke={LEFT_GAZE_COLOR} strokeWidth={PATH_WIDTH} fill="none" />
        )}
        {visibleEye !== "left" && (
          <path d={rightGazePath} stroke={RIGHT_GAZE_COLOR} strokeWidth={PATH_WIDTH} fill="none" />
        )}
        <path
          d={targetPath}
          stroke={TARGET_COLOR}
          strokeWidth={PATH_WIDTH}
          fill="none"
          strokeDasharray="4,2"
        />

        {targetMarkers}
        {leftGazeMarkers}
        {rightGazeMarkers}

        {latestTargetPosition}
        {latestLeftGazePosition}
        {latestRightGazePosition}
      </svg>
    );
  }, [data, width, height, theme, customHeight, visibleEye, sharedExtent]);

  const content = (
    <>
      {title && (
        <Text size={compact ? "xs" : "sm"} fw={600} mb="xs">
          {title}
        </Text>
      )}
      <AspectRatio ratio={1} w="100%" h={customHeight}>
        <Box ref={ref} w="100%" h="100%" pos="relative">
          {svgContent}
        </Box>
      </AspectRatio>

      {showLegend && (
        <Flex
          direction={{ base: "column", sm: "row" }}
          justify="center"
          align="center"
          gap={compact ? 6 : "xl"}
          mt={compact ? 4 : "sm"}
          wrap="wrap"
        >
          <Flex align="center" gap="xs">
            <Box
              w={compact ? 12 : 16}
              h={compact ? 6 : 8}
              style={{ backgroundColor: TARGET_COLOR }}
            />
            <Text size={compact ? "10px" : "sm"} style={{ lineHeight: 1.1 }}>
              {compact ? t("target") : t("targetPosition")}
            </Text>
          </Flex>
          {visibleEye !== "right" && (
            <Flex align="center" gap="xs">
              <Box
                w={compact ? 12 : 16}
                h={compact ? 6 : 8}
                style={{ backgroundColor: LEFT_GAZE_COLOR }}
              />
              <Text size={compact ? "10px" : "sm"} style={{ lineHeight: 1.1 }}>
                {compact ? t("left") : t("leftGazePosition")}
              </Text>
            </Flex>
          )}
          {visibleEye !== "left" && (
            <Flex align="center" gap="xs">
              <Box
                w={compact ? 12 : 16}
                h={compact ? 6 : 8}
                style={{ backgroundColor: RIGHT_GAZE_COLOR }}
              />
              <Text size={compact ? "10px" : "sm"} style={{ lineHeight: 1.1 }}>
                {compact ? t("right") : t("rightGazePosition")}
              </Text>
            </Flex>
          )}
        </Flex>
      )}

      {!compact && (
        <Text size="xs" c="dimmed" ta="center" mt="xs">
          {data?.length > 0
            ? t("trackingDataPoints", { count: data.length })
            : t("noDataAvailable")}
        </Text>
      )}
    </>
  );

  return showPaper ? (
    <Paper withBorder p="md" radius="md">
      {content}
    </Paper>
  ) : (
    <Box>{content}</Box>
  );
}
